/**
 * What the signed-in user is allowed to do with a CRM ticket.
 *
 * The API is the authority — this hook only decides which affordances to
 * render, so an operator is never offered a button that will bounce. It
 * mirrors the two rules the backend enforces today:
 *
 *   - assigning someone else needs ticket-management access;
 *   - closing a ticket (resolve / reject) needs it too, which is why an
 *     assignee could previously move a ticket through every status
 *     *except* resolved and get no explanation.
 *
 * Assignees keep everything they need to work the ticket: status moves
 * short of closing it, interactions, disposition, and escalation upward.
 *
 * When the access catalogue does not describe `crm.tickets` at all we fall
 * back to the role claim rather than locking managers out of their own
 * queue.
 */
import { useMemo } from 'react';
import { useAuth } from '@/auth/AuthProvider';
import { useAccess } from '@/features/AccessProvider';
import type { CrmTicket } from '@/api/bi/crm';

const TICKETS_SLUG = 'crm.tickets';

/** Roles that manage tickets when the BI access catalogue is unavailable. */
const MANAGER_ROLES: ReadonlySet<string> = new Set(['admin', 'manager']);

export interface TicketCapabilities {
  /** Current user id, or null when the claim is missing. */
  userId: number | null;
  isAssignee: boolean;
  isPrimaryAssignee: boolean;
  /** Ticket-management access — assign, reassign, close. */
  canManage: boolean;
  /** Status (short of closing), priority, resolution note, disposition. */
  canEdit: boolean;
  /** Replace the assignment list. */
  canAssign: boolean;
  /** Escalate upward — assignees can, viewers cannot. */
  canEscalate: boolean;
  /** Resolve or reject. */
  canClose: boolean;
  /** Log calls and notes. */
  canInteract: boolean;
  /** Why an action is unavailable — rendered as the disabled tooltip. */
  reason: {
    edit: string | null;
    assign: string | null;
    escalate: string | null;
    close: string | null;
  };
}

const NEEDS_TICKET_ACCESS = 'You need ticket access to change this ticket.';
const NEEDS_MANAGE = 'Needs ticket-management access — ask an admin or manager.';
const CLOSE_NEEDS_MANAGE =
  'Only ticket managers can resolve or reject. Escalate it instead, or ask your manager to close it.';

export function useTicketCapabilities(ticket: CrmTicket | null | undefined): TicketCapabilities {
  const { user } = useAuth();
  const { can, isReady, isSuperuser, resourceBySlug } = useAccess();

  return useMemo(() => {
    const userId = user?.id ?? null;
    const assignments = ticket?.assignments ?? [];
    const isAssignee =
      userId != null && assignments.some((a) => a.user?.id === userId);
    const isPrimaryAssignee =
      userId != null && assignments.some((a) => a.is_primary && a.user?.id === userId);

    const resource = resourceBySlug(TICKETS_SLUG);
    const catalogueKnowsManage = Boolean(
      resource && typeof resource.permissions?.manage === 'boolean',
    );
    const canManage =
      isSuperuser ||
      !isReady ||
      (catalogueKnowsManage
        ? can(TICKETS_SLUG, 'manage')
        : MANAGER_ROLES.has(String(user?.role ?? '')));

    const canView = isSuperuser || !isReady || can(TICKETS_SLUG);
    const canEdit = canManage || isAssignee || canView;
    const canInteract = canEdit;
    const canEscalate = canManage || isAssignee;

    return {
      userId,
      isAssignee,
      isPrimaryAssignee,
      canManage,
      canEdit,
      canAssign: canManage,
      canEscalate,
      canClose: canManage,
      canInteract,
      reason: {
        edit: canEdit ? null : NEEDS_TICKET_ACCESS,
        assign: canManage ? null : NEEDS_MANAGE,
        escalate: canEscalate ? null : NEEDS_MANAGE,
        close: canManage ? null : CLOSE_NEEDS_MANAGE,
      },
    };
  }, [can, isReady, isSuperuser, resourceBySlug, ticket?.assignments, user?.id, user?.role]);
}
